import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecoilValue } from 'recoil';
import { toast } from 'material-react-toastify';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { frontendSettingsAtom } from '../../../atoms/FrontendSettings';
import { PopularCategory } from '../../../interfaces/Category/PopularCategory';
import { useThemeColor } from '../hooks/useThemeColor';
import { getPopularCategories } from '../../../api/PopularCategory';
import { NavMenuItem } from '../../../interfaces/Settings/FrontendSettings';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

const PopularCategoriesMenu = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [open, setOpen] = useState(false);
  const [popularCategories, setPopularCategories] = useState<PopularCategory[]>(
    [],
  );

  const { secondary } = useThemeColor();

  useEffect(() => {
    getPopularCategories()
      .then((p) => setPopularCategories(p))
      .catch((err) => toast.error(err.message));
  }, []);

  return popularCategories.length ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        onClick={() => setOpen((x) => !x)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: open ? 2 : 0,
        }}
      >
        <EditableStyleWrapper path='footerMobileSettings.popularCategoriesTitleStyle'>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: secondary,
              ...settings.settingsV2?.footerMobileSettings
                ?.popularCategoriesTitleStyle,
            }}
          >
            {settings.settingsV1.popular_categories_text}
          </Typography>
        </EditableStyleWrapper>

        <EditableStyleWrapper path='footerMobileSettings.popularCategoriesChevronStyle'>
          <IconButton
            sx={{
              transform: open ? 'rotate(180deg)' : undefined,
              p: 0,
              color: secondary,
              ...settings.settingsV2?.footerMobileSettings
                ?.popularCategoriesChevronStyle,
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </EditableStyleWrapper>
      </Box>

      {open &&
        popularCategories.map((category) => (
          <EditableStyleWrapper
            key={category.id}
            path='footerMobileSettings.popularCategoriesItemStyle'
          >
            <Button
              sx={{
                color: secondary,
                fontSize: '14px',
                fontWeight: 600,
                width: 'fit-content',
                p: 0,
                minWidth: 0,
                textTransform: 'none',
                textAlign: 'left',
                ...settings.settingsV2?.footerMobileSettings
                  ?.popularCategoriesItemStyle,
              }}
            >
              {category.name}
            </Button>
          </EditableStyleWrapper>
        ))}
    </Box>
  ) : null;
};

type FooterMenuProps = {
  title: string;
  menuItems: NavMenuItem[];
};

const FooterMenu = ({ title, menuItems }: FooterMenuProps) => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [open, setOpen] = useState(false);

  const { secondary } = useThemeColor();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        onClick={() => setOpen((x) => !x)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: open ? 2 : 0,
        }}
      >
        <EditableStyleWrapper path='footerMobileSettings.menuTitleStyle'>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: secondary,
              ...settings.settingsV2?.footerMobileSettings?.menuTitleStyle,
            }}
          >
            {title}
          </Typography>
        </EditableStyleWrapper>

        <EditableStyleWrapper path='footerMobileSettings.menuChevronStyle'>
          <IconButton
            sx={{
              transform: open ? 'rotate(180deg)' : undefined,
              p: 0,
              color: secondary,
              ...settings.settingsV2?.footerMobileSettings?.menuChevronStyle,
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </EditableStyleWrapper>
      </Box>

      {open &&
        menuItems.map((item) => (
          <EditableStyleWrapper
            key={item.id}
            path='footerMobileSettings.menuItemStyle'
          >
            <Button
              sx={{
                color: secondary,
                fontSize: '14px',
                fontWeight: 600,
                width: 'fit-content',
                p: 0,
                minWidth: 0,
                textTransform: 'none',
                textAlign: 'left',
                ...settings.settingsV2?.footerMobileSettings?.menuItemStyle,
              }}
            >
              {item.name}
            </Button>
          </EditableStyleWrapper>
        ))}
    </Box>
  );
};

export const FooterMobile = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const { primary, secondary } = useThemeColor();

  const logo = settings.settingsV1.storefront_header_logo;

  return (
    <EditableStyleWrapper path='footerMobileSettings.rootStyle'>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pt: 3,
          px: 1.5,
          pb: 1.5,
          backgroundColor: primary,
          boxSizing: 'border-box',
          alignItems: 'center',
          mt: 'auto',
          ...settings.settingsV2?.footerMobileSettings?.rootStyle,
        }}
      >
        <PopularCategoriesMenu />

        <FooterMenu
          title={settings.settingsV1.storefront_footer_menu_one_title}
          menuItems={settings.footer1}
        />
        <FooterMenu
          title={settings.settingsV1.storefront_footer_menu_two_title}
          menuItems={settings.footer2}
        />

        <EditableStyleWrapper path='footerMobileSettings.dividerStyle'>
          <Divider
            sx={{
              width: '100%',
              backgroundColor: secondary,
              ...settings.settingsV2?.footerMobileSettings?.dividerStyle,
            }}
          />
        </EditableStyleWrapper>

        {logo ? (
          <EditableStyleWrapper path='footerMobileSettings.logoStyle'>
            <Box
              component='img'
              src={logo}
              sx={{
                cursor: 'pointer',
                height: 80,
                width: 'auto',
                ...settings.settingsV2?.footerMobileSettings?.logoStyle,
              }}
            />
          </EditableStyleWrapper>
        ) : (
          <Typography sx={{ color: secondary }}>
            {settings.settingsV1.storefront_navbar_text}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {!!settings.settingsV1.storefront_facebook_link && (
            <EditableStyleWrapper path='footerMobileSettings.socialMediaStyle'>
              <IconButton>
                <FacebookIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerMobileSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}

          {!!settings.settingsV1.storefront_twitter_link && (
            <EditableStyleWrapper path='footerMobileSettings.socialMediaStyle'>
              <IconButton>
                <XIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerMobileSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}

          {!!settings.settingsV1.storefront_instagram_link && (
            <EditableStyleWrapper path='footerMobileSettings.socialMediaStyle'>
              <IconButton>
                <InstagramIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerMobileSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}

          {!!settings.settingsV1.storefront_youtube_link && (
            <EditableStyleWrapper path='footerMobileSettings.socialMediaStyle'>
              <IconButton>
                <YouTubeIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerMobileSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}

          {!!settings.settingsV1.storefront_linkedin_link && (
            <EditableStyleWrapper path='footerMobileSettings.socialMediaStyle'>
              <IconButton>
                <LinkedInIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerMobileSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}
        </Box>

        <EditableStyleWrapper path='footerMobileSettings.copyrightStyle'>
          <Typography
            sx={{
              color: secondary,
              fontSize: '14px',
              fontWeight: 600,
              ...settings.settingsV2?.footerMobileSettings?.copyrightStyle,
            }}
          >
            © 2025 NNET E-Ticaret. Tüm hakları saklıdır.
          </Typography>
        </EditableStyleWrapper>
      </Box>
    </EditableStyleWrapper>
  );
};
